// src/components/SubscriptionManager.js
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { cancelSubscription } from '../services/paymentService';
import { revokeSubscription, getSubscriptionData, getDaysRemaining } from '../services/subscriptionService';

const SubscriptionManager = () => {
  // eslint-disable-next-line no-unused-vars
  const { currentUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const subscriptionData = getSubscriptionData();
  const daysRemaining = getDaysRemaining();
  
  const handleCancelSubscription = async () => {
    if (!subscriptionData || !subscriptionData.id) {
      setError('Subscription information not found');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await cancelSubscription(subscriptionData.id);
      
      if (result.success) {
        // Revoke pro status locally
        revokeSubscription();
        setSuccess('Your subscription has been cancelled. You will continue to have Pro access until the end of your billing period.');
        setCancelConfirm(false);
      } else {
        setError('Failed to cancel subscription. Please try again.');
      }
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      setError('An error occurred while cancelling your subscription.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!subscriptionData) {
    return (
      <div className="subscription-manager">
        <p>You don't have an active Pro subscription.</p>
      </div>
    );
  }

  return (
    <div className="subscription-manager">
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      
      <h3>Subscription Details</h3>
      <div className="subscription-info">
        <p><strong>Plan:</strong> {subscriptionData.plan || 'Pro'}</p>
        <p><strong>Status:</strong> {subscriptionData.status || 'Active'}</p>
        {daysRemaining !== null && (
          <p><strong>Renewal:</strong> {subscriptionData.cancelAtPeriodEnd 
            ? `Your Pro access expires in ${daysRemaining} days` 
            : `Renews in ${daysRemaining} days`}
          </p>
        )}
      </div>
      
      {!cancelConfirm ? (
        <motion.button 
          className="cancel-button"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setCancelConfirm(true)}
          disabled={isLoading || subscriptionData.cancelAtPeriodEnd}
        >
          {subscriptionData.cancelAtPeriodEnd 
            ? 'Subscription Cancelled' 
            : 'Cancel Subscription'}
        </motion.button>
      ) : (
        <div className="confirm-cancel">
          <p>Are you sure you want to cancel your Pro subscription?</p>
          <div className="button-group">
            <motion.button
              className="confirm-button"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleCancelSubscription}
              disabled={isLoading}
            >
              {isLoading ? 'Processing...' : 'Yes, Cancel'}
            </motion.button>
            <motion.button
              className="cancel-button"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setCancelConfirm(false)}
              disabled={isLoading}
            >
              No, Keep Subscription
            </motion.button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionManager;
